import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSpriteMessage, updateActorCustomData } from "../../store/sceneSlice";
import textToSpeechManager from "../../utils/TextToSpeechManager";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

const SCAN_COOLDOWN_MS = 5000;
const QUEUE_LIMIT = 5;
const CAMERA_POLL_MS = 400;
const READY_STATE_HAVE_DATA = 2;
const SPEECH_IDLE_POLL_MS = 200;
const SPEECH_IDLE_MAX_WAIT_MS = 6000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getResultText = (result) => {
  if (!result) return "";
  if (typeof result.getText === "function") return result.getText();
  if (typeof result.text === "string") return result.text;
  return String(result || "");
};

const isNotFoundError = (err) => {
  if (!err) return true;
  const name = String(err?.name || "");
  return name === "NotFoundException";
};

const BarcodeScanController = ({ selectedActorId }) => {
  const dispatch = useDispatch();
  const { scenes, currentSceneIndex, qrScanEnabled } = useSelector((s) => s.scene);

  const actorInfo = useMemo(() => {
    const scene = scenes?.[currentSceneIndex];
    const actors = scene?.actors || [];
    if (selectedActorId) {
      const target = actors.find((a) => a.id === selectedActorId) || actors[0] || null;
      return {
        id: target?.id || null,
        lang: target?.speechSettings?.lang || "en-US",
      };
    }
    const fallback = actors[0] || null;
    return {
      id: fallback?.id || null,
      lang: fallback?.speechSettings?.lang || "en-US",
    };
  }, [scenes, currentSceneIndex, selectedActorId]);

  const actorId = actorInfo?.id || null;
  const actorLang = actorInfo?.lang || "en-US";

  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const lastSeenRef = useRef(new Map());
  const activeRef = useRef(false);
  const lastErrorLogRef = useRef(0);
  const controlsRef = useRef(null);
  const startedRef = useRef(false);

  const waitForSpeechIdle = async () => {
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!synth) return;
    if (!synth.speaking && !synth.pending) return;
    const start = Date.now();
    while ((synth.speaking || synth.pending) && Date.now() - start < SPEECH_IDLE_MAX_WAIT_MS) {
      await delay(SPEECH_IDLE_POLL_MS);
    }
  };

  const pumpQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (activeRef.current && queueRef.current.length) {
        const item = queueRef.current.shift();
        if (!item?.text) continue;
        try {
          await waitForSpeechIdle();
          const spoke = await textToSpeechManager.speak(item.text, item.lang);
          if (!spoke) {
            await delay(1200);
          }
        } catch (err) {
          console.warn("QR speech failed:", err);
        }
      }
    } finally {
      processingRef.current = false;
    }
  };

  const enqueueSpeech = (text) => {
    const queue = queueRef.current;
    while (queue.length >= QUEUE_LIMIT) {
      queue.shift();
    }
    queue.push({ text, lang: actorLang });
    pumpQueue();
  };

  const handleDetection = (rawText) => {
    if (!activeRef.current) return;
    const text = String(rawText || "").trim();
    if (!text) return;
    const now = Date.now();
    const lastAt = lastSeenRef.current.get(text) || 0;
    if (now - lastAt < SCAN_COOLDOWN_MS) return;
    lastSeenRef.current.set(text, now);

    if (actorId) {
      dispatch(updateActorCustomData({
        actorId,
        data: {
          qrLastScan: text,
          qrLastScanAt: new Date().toISOString()
        }
      }));
      dispatch(showSpriteMessage({ actorId, message: text }));
    }
    enqueueSpeech(text);
  };

  useEffect(() => {
    activeRef.current = qrScanEnabled;
    if (!qrScanEnabled) {
      queueRef.current = [];
      processingRef.current = false;
    }
    return () => {
      activeRef.current = false;
    };
  }, [qrScanEnabled]);

  useEffect(() => {
    if (!qrScanEnabled) {
      return undefined;
    }

    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_93,
      BarcodeFormat.ITF,
      BarcodeFormat.CODABAR,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
    ]);
    const reader = new BrowserMultiFormatReader(hints);
    const handleResult = (result, err) => {
      if (cancelled) return;
      if (result) {
        const text = getResultText(result);
        handleDetection(text);
      }
      if (err && !isNotFoundError(err)) {
        const now = Date.now();
        if (now - lastErrorLogRef.current > 1500) {
          lastErrorLogRef.current = now;
          console.warn("QR decode error:", err);
        }
      }
    };

    const tryStart = async () => {
      if (cancelled) return;
      if (startedRef.current) return;
      const data = window.humanDetectionData || {};
      const videoEl = data.videoEl;
      const cameraActive = data.cameraActive;
      if (!cameraActive || !videoEl || videoEl.readyState < READY_STATE_HAVE_DATA) {
        setTimeout(tryStart, CAMERA_POLL_MS);
        return;
      }

      try {
        if (typeof reader.decodeFromVideoElement === "function") {
          const controls = await reader.decodeFromVideoElement(videoEl, handleResult);
          controlsRef.current = controls || null;
          startedRef.current = true;
          return;
        }
        console.warn("QR scanner: no supported video decode method found.");
      } catch (err) {
        console.warn("QR scanner failed to start:", err);
        setTimeout(tryStart, CAMERA_POLL_MS);
      }
    };

    tryStart();

    return () => {
      cancelled = true;
      startedRef.current = false;
      if (controlsRef.current?.stop) {
        try {
          controlsRef.current.stop();
        } catch (err) {
          console.warn("QR scanner stop failed:", err);
        }
      }
      controlsRef.current = null;
      try {
        if (typeof reader.stopContinuousDecode === "function") {
          reader.stopContinuousDecode();
        }
      } catch (err) {
        console.warn("QR scanner cleanup failed:", err);
      }
    };
  }, [qrScanEnabled, actorId, actorLang, dispatch]);

  return null;
};

export default BarcodeScanController;
